import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Snackbar, Alert, CircularProgress } from "@mui/material";

const GuestListLayer = () => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [token, setToken] = useState("");
  const [totalItems, setTotalItems] = useState(0);
  const navigate = useNavigate();
  const [serialNumbers, setSerialNumbers] = useState([]);
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    setToken(storedToken);
  }, [users]);

  useEffect(() => {
    if (token) {
      fetchUser();
    }
  }, [token, currentPage, itemsPerPage, searchTerm]);

  const fetchUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/get-all-guests?page=${currentPage}&limit=${itemsPerPage}&search=${searchTerm}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      // console.log("Response received:", response);
      const startSerial = (currentPage - 1) * itemsPerPage + 1; // Start serial for the current page
      const usersWithSerial = response.data.data.map((item, index) => ({
        ...item,
        serial: startSerial + index, // Calculate serial number correctly
        statusToggle: !item.status,
      }));
      setUsers(usersWithSerial);
      setSerialNumbers(usersWithSerial.map((t) => t.serial));
      setTotalItems(response.data.pagination.totalItems); // Set totalItems for pagination
      setTotalPages(response.data.pagination.totalPages);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      setError("Failed to fetch users.");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
    }
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1);
  };

  const handleEditUser = (guest) => {
    navigate("/edit-guest", { state: { guest } });
  };

  // snackbar
  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleToggleStatus = async (guest) => {
    try {
      const updatedStatus = !guest.status; // Toggle the current status
      const response = await axios.put(
        `${process.env.REACT_APP_INSTITUTION_BASEURL}/guest-status-update/${guest._id}`,
        { status: updatedStatus },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200) {
        // Update the status locally
        setUsers((prevGuest) =>
          prevGuest.map((b) =>
            b._id === guest._id ? { ...b, status: updatedStatus } : b
          )
        );
        setSnackbarMessage("Status updated successfully!");
        setSnackbarSeverity("success");
        setOpenSnackbar(true);
      }
    } catch (error) {
      console.error("Failed to toggle guest status:", error);
      setSnackbarMessage("Failed to update status. Please try again.");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {loading && (
        <div className="loader-container" style={styles.loaderContainer}>
          <CircularProgress />
        </div>
      )}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <form
            className="search px-10"
            style={{
              border: "1px solid #6b7280",
              display: "flex",
              alignItems: "center",
              borderRadius: "10px",
            }}
            onSubmit={(e) => e.preventDefault()}
          >
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{ marginRight: "10px" }}
            />
            <input
              type="text"
              className="bg-base h-40-px"
              name="search"
              onChange={(e) => {
                let value = e.target.value;

                // Remove leading/trailing spaces & allow only alphanumeric and spaces
                value = value.replace(/[^a-zA-Z0-9\s]/g, "").trimStart();

                setSearchTerm(value);
                setCurrentPage(1); // Reset to the first page
              }}
              value={searchTerm}
              placeholder="Search"
            />
          </form>
        </div> 
      </div>
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th scope="col">
                  <div className="d-flex align-items-center gap-10">S.L</div>
                </th>
                <th scope="col">Guest Id</th>
                <th scope="col">Name</th>
                <th scope="col">Email</th>
                <th scope="col">Created At</th>
                <th scope="col" className="text-center">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {users.length > 0 ? (
                users.map((user, index) => (
                  <tr>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        {user.serial}
                      </div>
                    </td>
                    <td>{user.guest_id}</td>
                    <td>
                      {" "}
                      <div className="d-flex align-items-center">
                        <img
                          src={
                            user.profile_url ||
                            "assets/images/user-grid/user-grid-img14.png"
                          }
                          alt="Wowdash"
                          className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden"
                        />
                        <div className="flex-grow-1">
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {user.name}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                            {user.email}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <span className="text-md mb-0 fw-normal text-secondary-light">
                          {new Date(
                          user.createdAt
                        ).toLocaleDateString()}{" "}
                        {new Date(
                          user.createdAt
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td className="text-center">
                      <button
                        onClick={() => handleToggleStatus(user)}
                        className={`bg-${
                          user.status ? "success" : "danger"
                        }-focus text-${
                          user.status ? "success" : "danger"
                        }-600 border border-${
                          user.status ? "success" : "danger"
                        }-main px-24 py-4 radius-4 fw-medium text-sm`}
                        style={{ cursor: "pointer" }}
                      >
                        {user.status ? "Active" : "Inactive"}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No guest found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
          Showing{" "}
    {users.length === 0 
      ? "0" 
      : `${(currentPage - 1) * itemsPerPage + 1}`}{" "} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            entries
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            {/* Previous Button */}
            <li className="page-item">
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                to="#"
                onClick={() => handlePageChange(currentPage - 1)}
                style={{
                  pointerEvents: currentPage === 1 ? "none" : "auto",
                  opacity: currentPage === 1 ? 0.5 : 1,
                }}
              >
                <Icon icon="ep:d-arrow-left" />
              </Link>
            </li>

            {/* Current Page Number */}
            <li className="page-item">
              <span className="page-link bg-primary-600 text-white fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md">
                {currentPage}
              </span>
            </li>

            {/* Next Button */}
            <li className="page-item">
                            <Link
                              className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                              to="#"
                              onClick={() => handlePageChange(currentPage + 1)}
                              style={{
                                pointerEvents: currentPage === totalPages ? "none" : "auto",
                                opacity: currentPage === totalPages ? 0.5 : 1,
                              }}
                            >
                              <Icon icon="ep:d-arrow-right" />
                            </Link>
                          </li>
          </ul>
        </div>
        <Snackbar
          open={openSnackbar}
          autoHideDuration={3000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </div>
    </div>
  );
};

// Add CSS for loader
const styles = {
  loaderContainer: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    zIndex: 1000,
  },
  cardContent: (loading) => ({
    opacity: loading ? 0.5 : 1,
  }),
};

export default GuestListLayer;
